import React, { Component } from "react";
import { Box, Typography, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    
    // Log error to monitoring service in production
    if (process.env.NODE_ENV === "production") {
      // Could integrate with Sentry, LogRocket, etc.
      console.error("Error caught by boundary:", error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback, componentName = "Component" } = this.props;

      // Allow custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px",
            minHeight: "200px",
            backgroundColor: "rgba(239, 68, 68, 0.05)",
            borderRadius: "12px",
            border: "1px solid rgba(239, 68, 68, 0.2)",
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: "3rem",
              color: "#ef4444",
              marginBottom: "16px",
            }}
            aria-hidden="true"
          />
          <Typography
            variant="h6"
            sx={{
              color: "#ef4444",
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            Something went wrong
          </Typography>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            {componentName} failed to load. Please try again.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            aria-label={`Retry loading ${componentName}`}
            sx={{
              color: "#ef4444",
              borderColor: "#ef4444",
              "&:hover": {
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                borderColor: "#ef4444",
              },
            }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
